import { FormControl, InputLabel, MenuItem, Select, TextField, Checkbox, FormControlLabel, Button, Autocomplete } from '@mui/material';
import React, { useContext, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next';
import { MoreHorizOutlined } from '@mui/icons-material';
import DelimiterDialog from '../../../../TemplateProperties/DelimiterDialog';
import _, { cloneDeep, debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { eachDeep } from 'deepdash-es/standalone';
import { ParentScreenCtx } from '../../../../../pages/workbench_build/WorkbenchBuild';
import { treeActions } from '../../../../../redux/slices/treeSlice';
import { httpStatusCodes } from '../../../../../constants/constantList';
import WorkflowSessionModal from '../../../../ItemProperties/WorkflowSessionModal';

const SendDataControls = ({ connectionDetails, handleBodyContent, sendSynchronousNodeData, sendSynchronousGetDataNodeData, screen, disableControl, useConnection, repoPDC, direction }) => {
    const { t } = useTranslation();
    const parentScreen = useContext(ParentScreenCtx);
    
    const [sendData, setSendData] = useState(t("SELECT"));
    const [getData, setGetData] = useState(t("SELECT"));
    const [dialogState, setDialogState] = useState(false);
    const [dataTerminator, setDataTerminator] = useState(null);
    const [outTime, setOutTime] = useState(0);
    const [content, setContent] = useState(null);
    const [selectedAdapterType, setSelectedAdapterType] = useState("");
    const { rightTree } = useSelector(state => state.treeData?.[parentScreen] || {});
    const rightTreeData = useSelector(state => state.treeData?.[parentScreen]?.rightTree?.tree?.body?.translatedData?.[0]?.children)
    const [synchronousNodes, setSynchronousNodes] = useState([]);
    const [synchronousGetDataNodes, setSynchronousGetDataNodes] = useState([]);
    const selectedNode = useSelector(state => state.treeData?.workflow?.rightTree?.tree?.body?.selectedNode);
    const translatedData = useSelector(state => state.treeData?.workflow?.rightTree?.tree?.body?.translatedData);
    const dispatch = useDispatch();

    // HTTP Response Code Override states
    const [overrideResponseCode, setOverrideResponseCode] = useState(false);
    const [responseCode, setResponseCode] = useState(t("SELECT"));

    // Content Type Override states
    const [overrideContentType, setOverrideContentType] = useState(false);
    const [contentType, setContentType] = useState(t("SELECT"));

    // Workflow Session Modal states
    const [valueLabel, setValueLabel] = useState("");
    const [workflowSessionModal, setWorkflowSessionModal] = useState({
        open: false,
        isFirstTime: false,
    });

    // Content Types 
    const contentTypes = [
        "text/html",
        "text/plain",
        "application/json",
        "application/octet-stream",
    ];

    const getFontWeight = (
        control,
        currentValue,
        defaultValue,
        useConnection
    ) => {
        if (!repoPDC) return "normal";
        if (!(control in repoPDC) && currentValue === defaultValue) return "normal";
        return (repoPDC?.[control] !== currentValue || !(control in repoPDC)) &&
            useConnection === "preDefined"
            ? "bold"
            : "normal";
    };

    const fontWeights = useMemo(
        () => ({
            SYNC_GET_DATA_ID: getFontWeight(
                "SYNC_GET_DATA_ID",
                getData,
                t("SELECT"),
                useConnection
            ),
            HTTP_RESPONSE_CODE: getFontWeight(
                "HTTP_RESPONSE_CODE",
                responseCode,
                t("SELECT"),
                useConnection
            ),
            HTTP_CONTENT_TYPE: getFontWeight(
                "HTTP_CONTENT_TYPE",
                contentType,
                t("SELECT"),
                useConnection
            ),
        }),
        [
            getData,
            responseCode,
            contentType,
            connectionDetails,
            useConnection,
            repoPDC,
            t
        ]
    );

    const delimDisplay = (val) => {
        if (val === undefined) return '';
        if (val === "CR" || val === "%0D" || val === "\r") return "CR";
        if (val === "LF" || val === "%0A" | val === "\n") return "LF";
        if (val === "CRLF" || val === "%0D%0A" || val === "\r\n") return "CRLF";

        try { val = decodeURIComponent(val) }
        catch (e) {
            //case - malformed URI
            //console.log(`delimDisplay - URI malformed, can not decode - ${props.label} '${val}' - Displaying raw.`) 
        }
        return val.length < 3 ?
            val.split('').map(e => `ASCII ${e.charCodeAt(0)} (${e})`).join(', ')
            : val;
    }

    const handleDataTerminator = (val) => {
        if (val) {
            setDataTerminator(delimDisplay(val, true));
            onInputChange('EOD_TERMINATOR', encodeURIComponent(val) || null);
        } else {
            onInputChange('EOD_TERMINATOR', encodeURIComponent(val) || null);
            setDataTerminator(val)
        }
    }

    const onInputChange = (name, value) => {
      if(screen === "SEND_DATA" || screen === "GET_DATA"){
        if (name === "SYNC_SEND_DATA_ID" || name === "SYNC_GET_DATA_ID")
            setContent({ [name]: value });
        else
            setContent({add: { [name]: value }});
        (["TIMEOUT", "EOD_TERMINATOR"].includes(name)) && updateConnectedNode(`SYNC_GET_DATA_${name}`, value);
      } else {
        setContent((prev => ({
          ...prev,
          [name]: value
        })))
      }
    }

    // Handle override response code checkbox change
    const handleOverrideResponseCodeChange = (checked) => {
        setOverrideResponseCode(checked);
       
        if (!checked) {
            setResponseCode(t("SELECT"));
            setContent({add:{HTTP_RESPONSE_CODE: null, OVERRIDE_HTTP_RESPONSE_CODE:null}});
        } else{
            onInputChange('OVERRIDE_HTTP_RESPONSE_CODE', 'TRUE');
        }
    }

    // Handle response code dropdown change
    const handleResponseCodeChange = (value) => {
        setResponseCode(value);
        onInputChange('HTTP_RESPONSE_CODE', value);
    }

    // Handle override content type checkbox change
    const handleOverrideContentTypeChange = (checked) => {
        setOverrideContentType(checked);
        if (!checked) {
            setContentType(t("SELECT"));
            setContent({add:{HTTP_CONTENT_TYPE: null, OVERRIDE_HTTP_CONTENT_TYPE:null}});
        } else{
            onInputChange('OVERRIDE_HTTP_CONTENT_TYPE', 'TRUE');
        }
    }

    // Handle content type dropdown change
    const handleContentTypeChange = (value) => {
        setContentType(value);
        onInputChange('HTTP_CONTENT_TYPE', value);
    }

    // Handle workflow session modal response
    const workflowMappings = {
        [t("LBL_STATUS_CODES")]: {
            stateSetter: setResponseCode,
            key: "HTTP_RESPONSE_CODE",
            value: responseCode !== t("SELECT") ? responseCode : null,
            inputType: "text",
        },
        [t("LBL_CONTENT_TYPE")]: {
            stateSetter: setContentType,
            key: "HTTP_CONTENT_TYPE",
            value: contentType !== t("SELECT") ? contentType : null,
            inputType: "text",
        },
    };

    const valuesHandler = (value) => {
        if (workflowMappings[valueLabel]) {
            const { stateSetter, key } = workflowMappings[valueLabel];
            stateSetter(value);
            onInputChange(key, value);
        }
    };

    function updateConnectedNode(key, value) {
        const updatedData = eachDeep(cloneDeep(translatedData), (obj) => {
            if(obj?.ID !== selectedNode?.SYNC_SEND_DATA_ID)
                return obj;

            // Ensure OUTBOUND object exists
            if (!obj.OUTBOUND) {
                obj.OUTBOUND = {};
            }

            if (value === "" || value === null)
                obj?.OUTBOUND.hasOwnProperty(key) && delete obj?.OUTBOUND[key];
            else
                obj.OUTBOUND[key] = value

            return obj;
        });
        
        dispatch(
            treeActions.setNewRightTreeTranslatedData({
                screen: parentScreen,
                type: rightTree.currentTab,
                data: updatedData,
            })
        );
    }

    const debouncedContentUpdate = debounce(handleBodyContent, 200);

    useEffect(() => {
        setDataTerminator(connectionDetails?.EOD_TERMINATOR ? delimDisplay(connectionDetails?.EOD_TERMINATOR) : "");
        setDialogState(false);
        setOutTime(connectionDetails?.TIMEOUT || "0");
        setSendData(synchronousNodes.find(node => node.ID === selectedNode?.SYNC_SEND_DATA_ID)?.NAME || t("SELECT"));
        setSelectedAdapterType(synchronousNodes.find(node => node.ID === selectedNode?.SYNC_SEND_DATA_ID) || "");
        setGetData(synchronousGetDataNodes.find(node => node.ID === selectedNode?.SYNC_GET_DATA_ID)?.NAME || t("SELECT"));
        // Set override states from connection details
        setOverrideResponseCode(connectionDetails?.OVERRIDE_HTTP_RESPONSE_CODE === 'TRUE');
        setResponseCode(connectionDetails?.HTTP_RESPONSE_CODE || t("SELECT"));
        setOverrideContentType(connectionDetails?.OVERRIDE_HTTP_CONTENT_TYPE === 'TRUE');
        setContentType(connectionDetails?.HTTP_CONTENT_TYPE || t("SELECT"));
        
        setContent({ ...connectionDetails });
        // eslint-disable-next-line
    }, [connectionDetails?.TIMEOUT, connectionDetails?.EOD_TERMINATOR, connectionDetails?.OVERRIDE_HTTP_RESPONSE_CODE, connectionDetails?.HTTP_RESPONSE_CODE, connectionDetails?.OVERRIDE_HTTP_CONTENT_TYPE, connectionDetails?.HTTP_CONTENT_TYPE,connectionDetails?.SYNC_GET_DATA_ID, connectionDetails?.SYNC_SEND_DATA_ID, synchronousNodes, selectedNode?.SYNC_GET_DATA_ID, selectedNode?.SYNC_SEND_DATA_ID]);

    useEffect(() => {
        if (!_.isEqual(content, connectionDetails)) debouncedContentUpdate(content);
        // eslint-disable-next-line
    }, [content?.add?.TIMEOUT, content?.add?.EOD_TERMINATOR, content?.SYNC_SEND_DATA_ID,content?.SYNC_GET_DATA_ID, content?.add?.HTTP_RESPONSE_CODE, content?.add?.HTTP_CONTENT_TYPE, content?.add?.OVERRIDE_HTTP_RESPONSE_CODE, content?.add?.OVERRIDE_HTTP_CONTENT_TYPE]);

        useEffect(() => {
        const selectedNodeKeyArray = selectedNode?.key?.split('-');
        
        if(rightTreeData?.length > 0){
          const newData = [];
          const newGetDataNodes = [];
          eachDeep(rightTreeData, (obj) => {
            if(!obj?.key)
              return;
          

            const objKeyArray = obj?.key?.split('-');
            if(obj?.ACTION === "SEND_DATA"
              && obj?.SYNCHRONOUS === "TRUE" && ["HTTP", "SOCKET", "Web Service", "GraphQL"].includes(obj?.OUTBOUND?.ADAPTER_TYPE)){
                const compareLen = Math.min(selectedNodeKeyArray?.length, objKeyArray?.length);
                for(let i = 0; i < compareLen; i++){
                  const objKeyPart = parseInt(objKeyArray?.[i]) || objKeyArray?.[i];
                  const selectedKeyPart = parseInt(selectedNodeKeyArray?.[i]) || selectedNodeKeyArray?.[i];
                  if(objKeyPart < selectedKeyPart){
                    newData.push(obj);
                    break;
                  } else if(objKeyPart > selectedKeyPart){
                    break;
                  }
                }
              }

            // Collect GET_DATA nodes with HTTP adapter type and SYNCHRONOUS true
            if(obj?.ACTION === "GET_DATA"
              && obj?.SYNCHRONOUS === "TRUE" && (["HTTP", "BridgeGateHTTP", "BridgeGateWebService", "BridgeGateSocket", "GraphQL"].includes(obj?.INBOUND?.ADAPTER_TYPE))){
                const compareLen = Math.min(selectedNodeKeyArray?.length, objKeyArray?.length);
                for(let i = 0; i < compareLen; i++){
                  const objKeyPart = parseInt(objKeyArray?.[i]) || objKeyArray?.[i];
                  const selectedKeyPart = parseInt(selectedNodeKeyArray?.[i]) || selectedNodeKeyArray?.[i];
                  if(objKeyPart < selectedKeyPart){
                    newGetDataNodes.push(obj);
                    break;
                  } else if(objKeyPart > selectedKeyPart){
                    break;
                  }
                }
              }
          })
  
          setSynchronousNodes(newData);
          setSynchronousGetDataNodes(newGetDataNodes);
          sendSynchronousNodeData(newData);
          sendSynchronousGetDataNodeData(newGetDataNodes);
        }
        // eslint-disable-next-line
      }, [selectedNode?.key]);
    
    return (
      <div style={styles.sendDataControls}>
        {/* Send Data Selector */}

        {screen === "GET_DATA" && (
          <FormControl variant="outlined" className="width-100" size="small">
            <InputLabel id="sendDataLabel">{t("LBL_SEND_DATA")}</InputLabel>
            <Select
              className={"width-100"}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                  },
                },
              }}
              name={"SYNC_SEND_DATA_ID"}
              labelId="sendDataLabel"
              label={t("LBL_SEND_DATA")}
              id="sendData"
              value={sendData}
              onChange={(e) => {
                setSendData(e.target.value);
                let selectedSyncNode = synchronousNodes.find(
                  (node) => node?.NAME === e.target.value
                );
                setSelectedAdapterType(selectedSyncNode);
                e.target.value !== t("SELECT")
                  ? onInputChange("SYNC_SEND_DATA_ID", selectedSyncNode?.ID)
                  : onInputChange("SYNC_SEND_DATA_ID", null);
              }}>
              {synchronousNodes.length ? (
                [{ NAME: t("SELECT") }, ...synchronousNodes]?.map(
                  (obj, key) => (
                    <MenuItem key={key} value={obj?.NAME}>
                      {obj?.NAME}
                    </MenuItem>
                  )
                )
              ) : (
                <MenuItem key={0} value={t("SELECT")}>
                  {t("SELECT")}
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}

        {/* Get Data Selector for SEND_DATA screen */}
        {(screen === "SEND_DATA" || direction === "OUT") && (
          <FormControl variant="outlined" className="width-100" size="small">
            <InputLabel id="getDataLabel">{t("LBL_GET_DATA")}</InputLabel>
            <Select
              className={"width-100"}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                  },
                },
              }}
              name={"SYNC_GET_DATA_ID"}
              labelId="getDataLabel"
              label={t("LBL_GET_DATA")}
              id="getData"
              value={getData}
              disabled={disableControl}
              sx={{ fontWeight: fontWeights.SYNC_GET_DATA_ID }}
              onChange={(e) => {
                let selectedGetDataNode = synchronousGetDataNodes.find(
                  (node) => node?.NAME === e.target.value
                );
                setGetData(e.target.value);
                e.target.value !== t("SELECT")
                  ? onInputChange("SYNC_GET_DATA_ID", selectedGetDataNode?.ID)
                  : onInputChange("SYNC_GET_DATA_ID", null);
              }}>
              {synchronousGetDataNodes.length ? (
                [{ NAME: t("SELECT") }, ...synchronousGetDataNodes]?.map(
                  (obj, key) => (
                    <MenuItem key={key} value={obj?.NAME}>
                      {obj?.NAME}
                    </MenuItem>
                  )
                )
              ) : (
                <MenuItem key={0} value={t("SELECT")}>
                  {t("SELECT")}
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}

        {/* HTTP Response Code Override Controls */}
        {(direction === "OUT" || screen === "SEND_DATA") && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={overrideResponseCode}
                  onChange={(e) => handleOverrideResponseCodeChange(e.target.checked)}
                  disabled={disableControl}
                />
              }
              label={t("BTN_OVERRIDE_STATUS_CODE")}
            />

            {overrideResponseCode && (
              <div style={{ display: "flex", gap: "4px" }}>
                <FormControl
                  variant="outlined"
                  className="width-100"
                  size="small">
                  <Autocomplete
                    freeSolo
                    size="small"
                    disabled={disableControl}
                    className="width-100"
                    options={httpStatusCodes}
                    value={responseCode}
                    onChange={(e, newValue) => handleResponseCodeChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("LBL_STATUS_CODES")}
                        variant="outlined"
                        size="small"
                        sx={{ input: { fontWeight: fontWeights.HTTP_RESPONSE_CODE } }}
                      />
                    )}
                  />
                </FormControl>
                <Button
                  variant="outlined"
                  disabled={disableControl}
                  size="small"
                  onClick={() => {
                    setValueLabel(t("LBL_STATUS_CODES"));
                    setWorkflowSessionModal({ open: true, isFirstTime: true });
                  }}
                  sx={{ width: "40px", minWidth: "40px" }}>
                  <MoreHorizOutlined fontSize="small" />
                </Button>
              </div>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={overrideContentType}
                  onChange={(e) =>
                    handleOverrideContentTypeChange(e.target.checked)
                  }
                  color="primary"
                  disabled={disableControl}
                />
              }
              label={t("BTN_OVERRIDE_CONTENT_TYPE")}
            />

            {overrideContentType && (
              <div
                style={{ display: "flex", gap: "4px" }}>
                <FormControl
                  variant="outlined"
                  className="width-100"
                  size="small">
                  <Autocomplete
                    freeSolo
                    size="small"
                    className="width-100"
                    disabled={disableControl}
                    options={contentTypes}
                    value={contentType}
                    onChange={(e, newValue) => handleContentTypeChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("LBL_CONTENT_TYPE")}
                        variant="outlined"
                        size="small"
                        sx={{ input: { fontWeight: fontWeights.HTTP_CONTENT_TYPE } }}
                      />
                    )}
                  />
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={disableControl}
                  onClick={() => {
                    setValueLabel(t("LBL_CONTENT_TYPE"));
                    setWorkflowSessionModal({ open: true, isFirstTime: true });
                  }}
                  sx={{ width: "40px", minWidth: "40px" }}>
                  <MoreHorizOutlined fontSize="small" />
                </Button>
              </div>
            )}
          </>
        )}

        {/* Socket-specific controls */}
        {selectedAdapterType?.OUTBOUND?.ADAPTER_TYPE === "SOCKET" && screen === "GET_DATA" && (
          <>
            <FormControl className="width-100" size="small">
              <TextField
                size="small"
                variant="outlined"
                className="width-100 delim-button"
                //  disabled={disableControl || false}
                label={t("LBL_EOD_TERMINATOR")}
                value={dataTerminator || t("TXT_NO_DELIM_CHOSEN")}
                onClick={() => setDialogState(true)}
                InputProps={{
                  readOnly: true,
                  sx: { textAlign: "center" },
                  inputProps: { style: { textAlign: "center" } },
                }}
              />
            </FormControl>
            <DelimiterDialog
              modalOpen={dialogState}
              close={() => setDialogState(false)}
              handleInput={handleDataTerminator}
              val={dataTerminator || ""}
              name={"EOD_TERMINATOR"}
              from={"EOD_TERMINATOR"}
            />
            <FormControl className="width-100" size="small">
              <TextField
                variant="outlined"
                size="small"
                label={t("LBL_TIMEOUT")}
                type="number"
                // disabled={disableControl || false}
                value={outTime}
                // sx={{ input: { fontWeight: fontWeights.TIMEOUT } }}

                onChange={(e) => {
                  const inputValue = e.target.value;
                  const numericValue = inputValue.replace(/\D/g, "");
                  if (numericValue.length <= 9) {
                    setOutTime(numericValue);
                    onInputChange(e.target.name, numericValue || null);
                  }
                }}
                className={"width-100"}
                name={"TIMEOUT"}
                autoComplete="off"
              />
            </FormControl>
          </>
        )}

        {/* Workflow Session Modal */}
        <WorkflowSessionModal
          valueLabel={valueLabel}
          showAccountData={true}
          showCallingWorkflow={true}
          showSystemData={true}
          showWorkflowSession={true}
          showDateAndTime={true}
          openModal={workflowSessionModal}
          setModalOpen={setWorkflowSessionModal}
          handleClose={() =>
            setWorkflowSessionModal({ open: false, isFirstTime: false })
          }
          initialValue={workflowMappings[valueLabel]?.value}
          valuesHandler={(value) => {
            valuesHandler(value);
          }}
          inputType={workflowMappings[valueLabel]?.inputType}
        />
      </div>
    );
}

export default SendDataControls;

const styles = {
    sendDataControls: {
        display: "flex",
        flexDirection: "column",
        gap: "15px"
    }
}